import React, { useState, useMemo, useCallback, useEffect } from 'react'
import VerificationCodeDialog from '@/components/VerificationCodeDialog/index'
import { useElectronicCardInfo, useUserInfo } from '@/hooks'
import { APIWeb } from '@/api/index'
import { copyToClipboard } from '@/utils'
import { Toast } from 'react-vant'
import CustomButton from '@/components/CustomButton'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import { useTranslation } from 'react-i18next'
import { useSendCode } from '@/hooks/useSendCode'
enum ActionType {
  None,
  Copy,
  RequestVerificationCode
}

interface Item {
  label: string
  val: React.ReactNode
  actionType: ActionType
}

export default React.memo(function CardInfo() {
  const { t } = useTranslation()
  const { cardInfo } = useElectronicCardInfo()
  const { userInfo, actions: userActions } = useUserInfo()
  const { sendCode, sendCodeLoading } = useSendCode()

  // 从 sessionStorage 恢复弹窗状态
  const [showVerificationModal, setShowVerificationModal] = useState(() => {
    const saved = sessionStorage.getItem('cvvModalOpen')
    return saved === 'true'
  })
  const [submitLoading, setSubmitLoading] = useState(false)
  //安全码（不缓存敏感数据）
  const [securityCard, setSecurityCard] = useState({
    cvv: "",
    expiry: "",
    card_no: "",
  })

  // 页面加载时刷新用户信息
  useEffect(() => {
    userActions.refreshUserInfo()
  }, [])

  // 保存弹窗状态到 sessionStorage
  useEffect(() => {
    if (showVerificationModal) {
      sessionStorage.setItem('cvvModalOpen', 'true')
    } else {
      sessionStorage.removeItem('cvvModalOpen')
    }
  }, [showVerificationModal])

  // 页面卸载时清除 sessionStorage
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('cvvModalOpen')
    }
  }, [])
  // 通用发送验证码函数
  const sendVerificationCode = useCallback(async () => {
    const data = await sendCode({
      account: userInfo?.email || '',
      scene: 'cvv_view'
    })
    return data
  }, [userInfo?.email, sendCode])

  // 验证码弹窗回调函数
  const handleVerificationSuccess = useCallback(async (code: string) => {
    setSubmitLoading(true)
    try {
      // 调用获取CVV的API，传递验证码
      const data = await APIWeb.getCVV({ code })
      // 将返回的cvv赋值给securityCode
      setSecurityCard({
        card_no: data?.card_no || '',
        cvv: data?.cvv || '',
        expiry: data?.expiry || '',
      })
      setShowVerificationModal(false)
    } catch (error) {
      console.error('获取CVV失败:', error)
      // 这里可以添加错误提示
    } finally {

      setSubmitLoading(false)
    }
  }, [])

  const handleResendCode = useCallback(async () => {
    await sendVerificationCode()
  }, [sendVerificationCode])

  const handleCloseVerification = useCallback(() => {
    setShowVerificationModal(false)
  }, [])

  const items = useMemo<Item[]>(
    () => ([
      {
        label: t('卡号'),
        val: securityCard?.card_no ? securityCard.card_no : (cardInfo?.card_no || '-'),
        actionType: securityCard?.card_no ? ActionType.Copy : ActionType.None
      },
      {
        label: t('有效期'),
        val: cardInfo?.expiry || '-',
        actionType: ActionType.None
      },
      { 
        label: t('安全码'), 
        val: securityCard?.cvv || '******', 
        actionType: securityCard?.cvv ? ActionType.Copy : ActionType.RequestVerificationCode 
      },
    ]),
    [cardInfo, securityCard, t]
  )

  const handleAction = useCallback(async (item: Item) => {
    if (item.actionType === ActionType.Copy) {
      // 复制功能
      copyToClipboard(item.val as string)
    } else if (item.actionType === ActionType.RequestVerificationCode) {
      // 发送验证码
      const data = await sendVerificationCode()
      if (data) {
        setShowVerificationModal(true)
      }

    }
  }, [sendVerificationCode])


  const Items = useMemo(
    () => items.map((item, index) => (
      <div key={index} className="w-full flex justify-between items-center border-solid border-[0.5px] border-[#E5E5E5] rounded-2xl py-4 pr-6 pl-4">
        <div>
          <div className="text-sm text-[#000000]/50 mb-2">{item.label}</div>
          <span className="text-gray-900 font-medium" >{item.val}</span>
        </div>
        <div>
          {item.actionType === ActionType.RequestVerificationCode && (
            <CustomButton
              onClick={() => handleAction(item)}
              size='small'
              loading={item.actionType === ActionType.RequestVerificationCode ? sendCodeLoading : false}
              disabled={item.actionType === ActionType.RequestVerificationCode ? sendCodeLoading : false}
              color='outline'
            >
              <span className='text-xs'>
                {sendCodeLoading ? t('发送中') : t('请求验证码')}
              </span>
            </CustomButton>
          )}
          {item.actionType === ActionType.Copy && (
            <CustomButton
              onClick={() => handleAction(item)}
              size='small'
              color='outline'
            >
              <span className='text-xs'>
                {t('复制')}
              </span>
            </CustomButton>
          )}
        </div>

      </div>
    )),
    [items, handleAction, sendCodeLoading]
  )

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-3.75 font-medium text-black">{t('卡片信息')}</span>}
      />
      <div className="bg-white h-full flex flex-col">

        {/* Card Info Items */}
        <div className="p-6 space-y-3 flex-1 overflow-y-auto">
          {Items}

          {/* <div className="space-y-1 w-full flex flex-col bg-[#F5F5F5] border-[0.5px] border-[#E5E5E5] rounded-2xl py-4 pr-6 pl-4">
          <div className="text-black/50">账单地址</div>
          <div className="flex justify-between items-center">
            <div className="space-y-1">
              <div className="text-black text-sm">4959 Colorado Blvd Denver</div>
              <div className="text-black text-sm">城市：Denver</div>
              <div className="text-black text-sm">邮编：80216</div>
              <div className="text-black text-sm">州：Colorado</div>
            </div>
            <div  className="ml-4 px-4 py-2 bg-[var(--primary-color)] text-white text-xs rounded-full transition-colors">
              复制
            </div>
          </div>
        </div> */}
        </div>

        {/* Verification Modal */}
        <VerificationCodeDialog
          visible={showVerificationModal}
          onClose={handleCloseVerification}
          account={userInfo?.email || ''}
          onVerificationSuccess={handleVerificationSuccess}
          onResendCode={handleResendCode}
          submitLoading={submitLoading}
          resendLoading={sendCodeLoading}
        />

      </div>
    </PageLayout>
  )
})